<template>
  <div class="deductions-table">
    <div v-if="deductions.length === 0" class="no-deductions">
      <i class="pi pi-info-circle"></i>
      <p>{{ t('Nenhuma dedução cadastrada') }}</p>
    </div>

    <div v-else class="deductions-list">
      <div
        v-for="deduction in deductions"
        :key="deduction.id"
        class="deduction-item"
      >
        <div class="deduction-info">
          <div class="deduction-header">
            <span class="deduction-amount">{{ n(parseFloat(String(deduction.amount)) || 0, 'currency') }}</span>
            <span class="deduction-date">{{ formatDate(deduction.created_at) }}</span>
          </div>
          <div class="deduction-description">{{ deduction.description }}</div>
          <div v-if="deduction.despesa_id" class="deduction-link">
            <i class="pi pi-link"></i>
            <span>{{ t('Vinculado à despesa') }} #{{ deduction.despesa_id }}</span>
          </div>
        </div>
        <div class="deduction-actions">
          <Button
            data-testid="deductions-delete-button"
            icon="pi pi-trash"
            class="p-button-sm p-button-danger p-button-text"
            :title="t('Deletar dedução')"
            @click="handleDelete(deduction.id)"
          />
        </div>
      </div>
    </div>

    <!-- Summary -->
    <div v-if="deductions.length > 0" class="deductions-summary">
      <div class="summary-row">
        <span class="summary-label">{{ t('Total de Deduções') }}:</span>
        <span class="summary-value">{{ n(totalAmount || 0, 'currency') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../composables/useDateFormatter';

interface Deduction {
  id: number | string;
  amount: number | string;
  description: string;
  despesa_id?: number | null;
  created_at?: string | null;
}

const props = withDefaults(defineProps<{
  deductions?: Deduction[];
}>(), {
  deductions: () => []
});

const emit = defineEmits<{
  (e: 'delete', deductionId: number | string): void;
}>();

const { t, n } = useI18n();

const { formatDate: formatDateLocale } = useDateFormatter();

const totalAmount = computed<number>(() => {
  return props.deductions.reduce((sum, d) => sum + (parseFloat(String(d.amount)) || 0), 0);
});

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  return formatDateLocale(new Date(dateString));
};

const handleDelete = (deductionId: number | string): void => {
  emit('delete', deductionId);
};
</script>

<style scoped>
.deductions-table {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.no-deductions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-color-secondary);
  text-align: center;
}

.no-deductions i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-deductions p {
  margin: 0;
  font-size: 1rem;
}

.deductions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.deduction-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 6px;
  transition: all 0.2s;
}

.deduction-item:hover {
  background: var(--surface-100);
  border-color: var(--surface-300);
}

.deduction-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.deduction-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.deduction-amount {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--red-600);
}

.deduction-date {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.deduction-description {
  font-weight: 500;
  color: var(--text-color);
  line-height: 1.4;
}

.deduction-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--primary-color);
}

.deduction-link i {
  font-size: 0.75rem;
}

.deduction-actions {
  display: flex;
  align-items: flex-start;
  margin-left: 1rem;
}

.deductions-summary {
  padding: 1rem;
  background: var(--primary-50);
  border: 2px solid var(--primary-200);
  border-radius: 6px;
  margin-top: 0.5rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-label {
  font-weight: 600;
  color: var(--text-color);
}

.summary-value {
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--red-600);
}
</style>
